<template>
  <div class="goods-group-setting">
    <div class="setting-label">商品管理</div>
    <el-button class="add-button" type="text" @click="pickerVisible = true">＋ 添加商品分组</el-button>
    <draggable
      v-if="selectedGroups.length"
      :list="selectedGroups"
      animation="200"
      handle=".drag-handle"
      class="selected-groups"
      @end="syncSelectedGroups"
    >
      <div v-for="group in selectedGroups" :key="group.group_id" class="selected-group">
        <i class="el-icon-rank drag-handle"></i>
        <span class="selected-name">{{ group.group_name }}</span>
        <i class="el-icon-close remove-group" @click="removeGroup(group.group_id)"></i>
      </div>
    </draggable>
    <p v-else class="empty-tip">暂未选择商品分组</p>
    <p class="setting-tip">拖动可调整 Tab 顺序，商品和排序会实时读取分组的最新数据。</p>

    <el-dialog title="选择商品分组" :visible.sync="pickerVisible" width="620px" append-to-body>
      <div class="picker-count">已选择 {{ selectedCount }} 个</div>
      <div v-loading="loading" class="group-tree-wrap">
        <el-tree
          ref="groupTree"
          :data="options"
          :props="treeProps"
          node-key="group_id"
          show-checkbox
          default-expand-all
          @check="handleCheck"
        >
          <span slot-scope="{ data }" class="group-node">
            <span class="group-name">{{ data.group_name }}</span>
            <span class="goods-count">{{ data.goods_count || 0 }} 件商品</span>
          </span>
        </el-tree>
      </div>
      <span slot="footer">
        <el-button @click="pickerVisible = false">取消</el-button>
        <el-button type="primary" @click="pickerVisible = false">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import Draggable from 'vuedraggable'
import { getVisibleGoodsGroups, getVisibleGoodsGroup } from '@/api/goods-group'

const normalizeListStyle = style => ({
  BIG: 'single',
  SMALL: 'row-two',
  MIXED: 'hybrid',
  DETAIL: 'column'
}[String(style || '').toUpperCase()] || style || 'single')

export default {
  name: 'GoodsGroupSettingSelector',
  components: { Draggable },
  data() {
    return {
      loading: false,
      pickerVisible: false,
      options: [],
      selectedCount: 0,
      refreshSequence: 0,
      treeProps: { label: 'group_name', children: 'children' }
    }
  },
  computed: {
    selectedGroups() {
      return Array.isArray(this.$$FormData.groups) ? this.$$FormData.groups : []
    }
  },
  mounted() { this.loadGroups() },
  methods: {
    async loadGroups() {
      this.loading = true
      try {
        const groups = await getVisibleGoodsGroups()
        this.options = (groups || []).map(parent => {
          const option = Object.assign({}, parent)
          const children = (parent.children || []).map(child => {
            const childOption = Object.assign({}, child)
            delete childOption.children
            return childOption
          })
          if (children.length) option.children = children
          else delete option.children
          return option
        })
        const ids = Array.isArray(this.$$FormData.group_ids) && this.$$FormData.group_ids.length
          ? this.$$FormData.group_ids
          : (this.$$FormData.group_id ? [this.$$FormData.group_id] : [])
        this.$set(this.$$FormData, 'group_ids', ids)
        this.selectedCount = ids.length
        this.$nextTick(() => {
          if (this.$refs.groupTree) this.$refs.groupTree.setCheckedKeys(ids)
        })
        if (ids.length && !this.selectedGroups.length) await this.refreshGroups(ids)
      } finally {
        this.loading = false
      }
    },
    handleCheck() {
      const ids = this.$refs.groupTree ? this.$refs.groupTree.getCheckedKeys() : []
      this.refreshGroups(ids)
    },
    async refreshGroups(ids) {
      this.selectedCount = ids.length
      const sequence = ++this.refreshSequence
      if (!ids.length) {
        this.applyGroups([])
        return
      }
      const groups = await Promise.all(ids.map(id => getVisibleGoodsGroup(id)))
      if (sequence !== this.refreshSequence) return
      const oldOrder = new Map(this.selectedGroups.map((group, index) => [String(group.group_id), index]))
      groups.sort((a, b) => {
        const ai = oldOrder.has(String(a.group_id)) ? oldOrder.get(String(a.group_id)) : Number.MAX_SAFE_INTEGER
        const bi = oldOrder.has(String(b.group_id)) ? oldOrder.get(String(b.group_id)) : Number.MAX_SAFE_INTEGER
        return ai - bi
      })
      this.applyGroups(groups.map(group => ({
        group_id: group.group_id,
        group_name: group.group_name,
        list_style: normalizeListStyle(group.list_style),
        goodsList: (group.goods || []).map(item => Object.assign({}, item, {
          goods_image: item.goods_image || item.thumbnail || item.small,
          goods_price: item.goods_price == null ? item.price : item.goods_price
        }))
      })))
    },
    applyGroups(groups) {
      const ids = groups.map(group => group.group_id)
      this.$set(this.$$FormData, 'groups', groups)
      this.$set(this.$$FormData, 'group_ids', ids)
      this.$$FormData.group_id = ids.length ? ids[0] : ''
      this.$$FormData.group_name = groups.map(group => group.group_name).join('、')
      this.$$FormData.goodsList = groups.length ? groups[0].goodsList : []
    },
    syncSelectedGroups() {
      this.applyGroups(this.selectedGroups.slice())
    },
    removeGroup(groupId) {
      const groups = this.selectedGroups.filter(group => String(group.group_id) !== String(groupId))
      this.applyGroups(groups)
      this.selectedCount = groups.length
      this.$nextTick(() => {
        if (this.$refs.groupTree) this.$refs.groupTree.setCheckedKeys(groups.map(group => group.group_id))
      })
    }
  }
}
</script>

<style scoped>
.goods-group-setting { padding: 16px; background: #fff; }
.setting-label { margin-bottom: 8px; color: #606266; }
.add-button { width: 100%; padding: 12px; border: 1px dashed #409eff; }
.selected-groups { margin-top: 12px; border: 1px solid #ebeef5; }
.selected-group { display: flex; align-items: center; height: 42px; padding: 0 12px; border-bottom: 1px dashed #ebeef5; }
.selected-group:last-child { border-bottom: 0; }
.drag-handle { margin-right: 10px; color: #c0c4cc; cursor: move; }
.selected-name { flex: 1; color: #606266; }
.remove-group { color: #c0c4cc; cursor: pointer; }
.remove-group:hover { color: #f56c6c; }
.empty-tip { margin: 12px 0 0; color: #909399; text-align: center; }
.setting-tip { margin: 10px 0 0; color: #909399; font-size: 12px; line-height: 1.6; }
.picker-count { padding: 8px 10px; color: #606266; background: #f5f7fa; border: 1px solid #ebeef5; border-bottom: 0; }
.group-tree-wrap { min-height: 160px; max-height: 420px; overflow-y: auto; border: 1px solid #ebeef5; }
.group-tree-wrap .el-tree { padding: 6px 0; }
.group-node { display: flex; flex: 1; align-items: center; justify-content: space-between; padding-right: 18px; }
.group-name { color: #409eff; }
.goods-count { color: #909399; font-size: 12px; }
</style>
